import axios, { type AxiosResponse } from "axios";
import React, { type ReactElement } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import pages from "../../../../constants/pages";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useResolvedPluginDisplayName from "../../../../hooks/useResolvedPluginDisplayName";
import useResourceVerifier from "../../../../hooks/useResourceVerifier";
import { ErrorBoundary, ErrorCard, LoadingCard } from "../../../errors";
import { validatePluginId } from "../../../errors/pluginValidation";
import PluginHomePage, { type PluginHomeLocationState } from "./PluginHomePage";

interface PluginResource {
	pluginId?: string;
	name?: string;
	active?: boolean;
	isCustomerManaged?: boolean;
	[key: string]: unknown;
}

interface PluginHomePageWithErrorBoundaryProps {
	children: ReactElement;
}

const PluginHomePageWithErrorBoundary = ({ children }: PluginHomePageWithErrorBoundaryProps): React.JSX.Element => {
	const { pluginId = "" } = useParams<{ pluginId: string }>();
	const location = useLocation();
	const authHeader = useAuthHeader();

	const {
		isLoading,
		resourceError: pluginError,
		resourceData: pluginData,
	} = useResourceVerifier<PluginResource>({
		id: pluginId,
		validateIdFn: validatePluginId,
		fetchResourceFn: () =>
			axios.get(apiRoutes.getPlugin(pluginId), { headers: authHeader }),
		validateResponseFn: (response: AxiosResponse<PluginResource>) =>
			Boolean(response?.data && !response.data.error && response.data.pluginId),
		authHeader,
		invalidIdMsg: "Invalid plugin ID format",
		timeoutMsg: "Timeout. Plugin may not exist or has been removed.",
	});

	const { displayName, isResolvingName } = useResolvedPluginDisplayName(pluginId, pluginData);

	const getStateForChildren = (): PluginHomeLocationState => {
		const nav = location.state as Partial<PluginHomeLocationState> | null;
		return {
			name: nav?.name ?? displayName,
			pluginId: String(pluginId),
			platformId: nav?.platformId,
		};
	};

	if (isLoading) {
		return (
			<LoadingCard
				title="Loading Plugin"
				message="Please wait while we load the plugin..."
				subMessage="If this takes more than a few seconds, the plugin may not exist."
			/>
		);
	}

	if (pluginError) {
		return (
			<ErrorCard
				title="Plugin Error"
				message={pluginError.message}
				returnPath="/settings/plugins"
				returnButtonText="Return to Plugins"
			/>
		);
	}

	if (pluginData && pluginData.isCustomerManaged !== true) {
		return <Navigate to={pages.pluginGroups(pluginId)} replace />;
	}

	/* Match sidebar: wait for platforms lookup when detail GET omits `name`. */
	if (!location.state && pluginData && isResolvingName) {
		return (
			<LoadingCard
				title="Loading Plugin"
				message="Please wait while we load plugin details..."
				subMessage="If this takes more than a few seconds, try again."
			/>
		);
	}

	return (
		<ErrorBoundary parentPath="/settings/plugins">
			{React.cloneElement(children, {
				...(children.props as Record<string, unknown>),
				state: getStateForChildren(),
			} as Record<string, unknown>)}
		</ErrorBoundary>
	);
};

export default PluginHomePageWithErrorBoundary;
